import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const refresh = useCallback(async () => {
        // Skip the unauthenticated /auth/me probe when we know there's no
        // session — we can't read the httpOnly session_token cookie from JS,
        // so we use a small non-sensitive localStorage flag (`glms_session`)
        // set on login and cleared on logout. This suppresses noisy 401s on
        // every public route (/, /pricing, /blog, /blog/:slug, /@handle).
        const hasSession = typeof window !== "undefined"
            && window.localStorage.getItem("glms_session") === "1";
        if (!hasSession) {
            setUser(null);
            setPlan(null);
            return null;
        }
        try {
            const data = await api.me();
            setUser(data);
            // Fetch plan in parallel (cheap, cached backend-side)
            try {
                const p = await api.planMe();
                setPlan(p.plan);
            } catch {
                setPlan(null);
            }
            return data;
        } catch (e) {
            // 401 means the cookie already expired — clear the flag
            if (typeof window !== "undefined") {
                window.localStorage.removeItem("glms_session");
            }
            setUser(null);
            setPlan(null);
            return null;
        }
    }, []);

    useEffect(() => {
        (async () => {
            await refresh();
            setLoading(false);
        })();
    }, [refresh]);

    const logout = useCallback(async () => {
        try {
            await api.logout();
        } catch {}
        if (typeof window !== "undefined") {
            window.localStorage.removeItem("glms_session");
        }
        setUser(null);
        setPlan(null);
        // FASE I · full page reload so the browser re-hits `/` and Next.js
        // SSR serves the premium landing (not the dormant SPA LandingV4Page).
        if (typeof window !== "undefined") {
            window.location.replace("/");
        }
    }, []);

    // Helper: check module unlock
    const hasModule = useCallback(
        (module_id) => {
            if (!plan) return false;
            const mods = plan.modules || [];
            return mods.includes("*") || mods.includes(module_id);
        },
        [plan],
    );

    const value = useMemo(
        () => ({ user, plan, loading, refresh, logout, setUser, hasModule }),
        [user, plan, loading, refresh, logout, hasModule],
    );
    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
